import React from 'react';
import { produce } from 'immer';
const applyPatches = (data, patches, variables) => patches.reduce((patchedData, patch) => produce(patchedData, draft => {
    patch(draft, {
        variables,
        complete: true,
        previousData: patchedData,
    });
}), data);
/**
 * Keeps `updateQuery` working for a query fetched with `fetchPolicy: 'no-cache'`.
 *
 * Apollo's `updateQuery` patches the cache, which a `no-cache` query never reads, so its patches would be lost.
 * Here they are applied to the query's last network result, held in state; the next network result (e.g. a
 * refetch) replaces them. A patch arriving before the first result — a mutation finishing while the query is still
 * loading — is held and applied once that result lands, as the response may predate the mutation. Patches must
 * therefore be safe to apply to a result that already contains their change, as replacing entries is.
 *
 * @example
 * ```ts
 * const queryResult = useUncachedQueryResult(useStatQuery(SomeQuery, {fetchPolicy: 'no-cache', nextFetchPolicy: 'no-cache'}));
 * queryResult.updateQuery(draft => {
 *     draft.items = [...draft.items.filter(item => item.id !== updatedItem.id), updatedItem];
 *     return draft;
 * });
 * ```
 */
export function useUncachedQueryResult(queryResult) {
    const { variables } = queryResult;
    const [patched, setPatched] = React.useState({
        source: queryResult.data,
        data: queryResult.data,
        pending: [],
    });
    let current = patched;
    if (patched.source !== queryResult.data) {
        const hasResult = queryResult.data !== undefined;
        current = {
            source: queryResult.data,
            data: hasResult ? applyPatches(queryResult.data, patched.pending, variables) : undefined,
            pending: hasResult ? [] : patched.pending,
        };
        setPatched(current);
    }
    const updateQuery = patch => {
        setPatched(previous => previous.data === undefined
            ? { ...previous, pending: [...previous.pending, patch] }
            : { ...previous, data: applyPatches(previous.data, [patch], variables) });
    };
    return { ...queryResult, data: current.data, updateQuery };
}
